import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SparklesIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ListBulletIcon,
  BookOpenIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { trackEngagement } from '../../hooks/usePWA';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import OutOfWordsSheet, {
  OutOfWordsInfo,
  outOfWordsFrom,
  sharedWordsMessage,
} from '../../components/billing/OutOfWordsSheet';
import {
  CurrentVersion,
  PendingEdits,
  SaveConflictDialog,
} from '../../components/story/EditConflicts';

type AiMode = 'continue' | 'suggest' | 'edit' | 'dialogue' | 'describe' | null;

interface Beat {
  id: string;
  summary: string | null;
  emotionalArc: string | null;
  content: string | null;
  version: number;
}

/** The owner's own save that hit a newer change, waiting for their choice. */
interface OwnConflict {
  target: string;
  current: CurrentVersion;
}

/** Where AI output lands: the container's own text, or one beat's text. */
const OWN = 'own';

const computeWords = (text: string) =>
  text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

/** A textarea that grows with its text, so the beats read as one continuous page. */
const AutoTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>((props, forwardedRef) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Collapsing to 'auto' to measure shrinks the scroll container, which clamps its
    // scrollTop; restore it so typing at the bottom of the page doesn't jump the view.
    const scroller = el.closest<HTMLElement>('.overflow-y-auto');
    const scrollTop = scroller?.scrollTop;
    el.style.height = 'auto';
    // scrollHeight excludes the border, which a border-box height must include.
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    if (scroller && scrollTop !== undefined) scroller.scrollTop = scrollTop;
  }, []);
  useLayoutEffect(fit, [props.value, fit]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        fit();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);
  return (
    <textarea
      rows={3}
      {...props}
      ref={(el) => {
        ref.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      }}
    />
  );
});
AutoTextarea.displayName = 'AutoTextarea';

const ChapterEditor: React.FC = () => {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('planned');
  const [format, setFormat] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chapterDirty, setChapterDirty] = useState(false);

  // AI panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState<AiMode>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [outOfWords, setOutOfWords] = useState<OutOfWordsInfo | null>(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [selection, setSelection] = useState<{ target: string; text: string } | null>(null);
  const selectedText = selection?.text || '';
  const [characters, setCharacters] = useState<any[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState('');

  // Beats: the container's pages are its own text followed by every beat's text, in order.
  const [scenesOpen, setScenesOpen] = useState(false);
  const [scenes, setScenes] = useState<Beat[]>([]);
  const [beatText, setBeatText] = useState<Record<string, string>>({});
  const [dirtyBeats, setDirtyBeats] = useState<string[]>([]);
  const [activeTarget, setActiveTarget] = useState<string | null>(null);
  const [readMode, setReadMode] = useState(false);
  // Viewers only read: the whole manuscript, no save, no AI. The API enforces the same.
  const [canEdit, setCanEdit] = useState(true);
  const beatRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  // The version each piece of text was loaded at; saves name it, and a newer one on the
  // server is a conflict (the owner decides, an editor's edit is parked for the owner).
  // Bumped as a save starts and ends: a refresh whose requests overlapped a save may have
  // read the text from before it, so it must not be applied.
  const saveSeq = useRef(0);
  const versions = useRef<{ chapter: number | null; beats: Record<string, number> }>({
    chapter: null,
    beats: {},
  });
  const [isOwner, setIsOwner] = useState(false);
  const [lastEdit, setLastEdit] = useState<{ name: string | null; at: string } | null>(null);
  const [conflicts, setConflicts] = useState<OwnConflict[]>([]);
  const [pendingKey, setPendingKey] = useState(0);
  const [stale, setStale] = useState(false);

  const dirty = chapterDirty || dirtyBeats.length > 0;
  const wordCount =
    computeWords(content) + scenes.reduce((sum, b) => sum + computeWords(beatText[b.id] ?? ''), 0);

  const acted = format === 'screenplay' || format === 'stage_play';
  const subLabel = acted
    ? t('stories.scenes.beatLabel', 'Beat')
    : t('stories.structure.scene', 'Scene');
  const subsTitle = acted
    ? t('stories.structure.beats', 'Beats')
    : t('stories.structure.scenesSub', 'Scenes');

  /** Show a loaded chapter as the clean starting point. */
  const applyChapter = useCallback((data: any) => {
    setTitle(data.title || '');
    setContent(data.content || '');
    setStatus(data.status || 'planned');
    versions.current.chapter = data.version ?? null;
    setLastEdit(data.updatedByName ? { name: data.updatedByName, at: data.updatedAt } : null);
    setChapterDirty(false);
    setStale(false);
  }, []);

  const applyScenes = useCallback((rows: Beat[]) => {
    setScenes(rows);
    setBeatText(Object.fromEntries(rows.map((b) => [b.id, b.content || ''])));
    versions.current.beats = Object.fromEntries(rows.map((b) => [b.id, b.version]));
    setDirtyBeats([]);
  }, []);

  const fetchChapter = useCallback(async () => {
    try {
      const [chapterRes, storyRes] = await Promise.all([
        axios.get(`/stories/${id}/chapters/${chapterId}`),
        axios.get(`/stories/${id}`),
      ]);
      applyChapter(chapterRes.data);
      setFormat(storyRes.data.format || '');
      setCharacters(storyRes.data.characters || []);
      setCanEdit(storyRes.data.role !== 'viewer');
      setIsOwner(storyRes.data.role === 'owner');
      if (storyRes.data.role === 'viewer') setAiOpen(false);
    } catch {
      toast.error(t('stories.loadError', 'Failed to load'));
      navigate(`/stories/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, chapterId, navigate, t, applyChapter]);

  const fetchScenes = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${id}/chapters/${chapterId}/scenes`);
      applyScenes(res.data);
    } catch {
      toast.error(t('stories.loadError', 'Failed to load'));
    }
  }, [id, chapterId, t, applyScenes]);

  useEffect(() => {
    fetchChapter();
    fetchScenes();
  }, [fetchChapter, fetchScenes]);

  const handleContentChange = (text: string) => {
    setContent(text);
    setChapterDirty(true);
  };

  const handleBeatChange = (beatId: string, text: string) => {
    setBeatText((prev) => ({ ...prev, [beatId]: text }));
    setDirtyBeats((prev) => (prev.includes(beatId) ? prev : [...prev, beatId]));
  };

  const textOf = (target: string) => (target === OWN ? content : (beatText[target] ?? ''));
  const setTextOf = (target: string, text: string) =>
    target === OWN ? handleContentChange(text) : handleBeatChange(target, text);

  // Saves compare against what is current when the request settles, so a keystroke typed
  // mid-save stays dirty instead of being marked saved.
  const latest = useRef({ title, content, status, beatText });
  latest.current = { title, content, status, beatText };

  /** What a 409 on a save means for that piece of text. */
  const conflictFrom = (err: any): { code: string; current?: CurrentVersion } | null => {
    const data = err?.response?.status === 409 ? err.response.data : null;
    return data?.code ? { code: data.code, current: data.current } : null;
  };

  /** Show the server's text for a target and treat it as the starting point. */
  const adopt = (target: string, current: CurrentVersion) => {
    if (target === OWN) {
      versions.current.chapter = current.version;
      setContent(current.content ?? '');
      if (current.title !== undefined) setTitle(current.title || '');
      if (current.status !== undefined) setStatus(current.status || 'planned');
      setChapterDirty(false);
    } else {
      versions.current.beats[target] = current.version;
      setBeatText((prev) => ({ ...prev, [target]: current.content ?? '' }));
      setDirtyBeats((prev) => prev.filter((b) => b !== target));
    }
  };

  const save = async () => {
    if (saving) return;
    saveSeq.current++;
    setSaving(true);
    const sentChapter = chapterDirty ? { title, content, status } : null;
    const sentBeats = dirtyBeats.map((beatId) => ({ beatId, content: beatText[beatId] ?? '' }));
    // One at a time: every write recounts the container's words from all its rows, and
    // parallel recounts can persist a total computed from a stale read.
    const writes: Array<{ target: string; run: () => Promise<any> }> = [
      {
        target: OWN,
        run: () =>
          sentChapter
            ? axios.patch(`/stories/${id}/chapters/${chapterId}`, {
                title: sentChapter.title || undefined,
                content: sentChapter.content,
                status: sentChapter.status,
                ...(versions.current.chapter ? { baseVersion: versions.current.chapter } : {}),
              })
            : Promise.resolve(null),
      },
      ...sentBeats.map((b) => ({
        target: b.beatId,
        run: () =>
          axios.patch(`/stories/${id}/chapters/${chapterId}/scenes/${b.beatId}`, {
            content: b.content,
            ...(versions.current.beats[b.beatId]
              ? { baseVersion: versions.current.beats[b.beatId] }
              : {}),
          }),
      })),
    ];
    type Outcome = 'saved' | 'failed' | 'conflict' | 'parked';
    const results: Outcome[] = [];
    const ownConflicts: OwnConflict[] = [];
    let parked = 0;
    let keptTyping = 0;
    // The server's own reason, when it gave one (shown instead of a bare "failed").
    let failure: string | null = null;
    for (const write of writes) {
      try {
        const res = await write.run();
        if (res?.data?.version !== undefined) {
          if (write.target === OWN) versions.current.chapter = res.data.version;
          else versions.current.beats[write.target] = res.data.version;
        }
        results.push('saved');
      } catch (err) {
        const conflict = conflictFrom(err);
        if (conflict?.code === 'save_conflict' && conflict.current) {
          ownConflicts.push({ target: write.target, current: conflict.current });
          results.push('conflict');
        } else if (conflict?.code === 'save_parked' && conflict.current) {
          // What was sent is safe with the owner; carry on from what's there now — unless
          // they kept typing meanwhile: then that text stays on screen, dirty.
          const now = latest.current;
          const untouched =
            write.target === OWN
              ? !!sentChapter &&
                now.content === sentChapter.content &&
                now.title === sentChapter.title &&
                now.status === sentChapter.status
              : now.beatText[write.target] ===
                sentBeats.find((b) => b.beatId === write.target)?.content;
          // Text typed meanwhile keeps its old base, so its next save parks too rather
          // than landing over the newer text.
          if (untouched) adopt(write.target, conflict.current);
          else keptTyping++;
          parked++;
          results.push('parked');
        } else {
          const message = (err as any)?.response?.data?.message;
          if (typeof message === 'string' && (err as any).response.status < 500) {
            failure = message;
          }
          results.push('failed');
        }
      }
    }
    const now = latest.current;
    if (
      sentChapter &&
      results[0] === 'saved' &&
      now.title === sentChapter.title &&
      now.content === sentChapter.content &&
      now.status === sentChapter.status
    ) {
      setChapterDirty(false);
    }
    const savedBeats = sentBeats
      .filter((b, i) => results[i + 1] === 'saved' && now.beatText[b.beatId] === b.content)
      .map((b) => b.beatId);
    setDirtyBeats((prev) => prev.filter((beatId) => !savedBeats.includes(beatId)));
    if (ownConflicts.length) setConflicts((prev) => [...prev, ...ownConflicts]);
    if (parked) {
      toast(
        keptTyping
          ? t(
              'stories.conflicts.parkedKeptToast',
              'Someone changed this while you were editing. What you sent went to the owner to decide; what you typed since is still here and goes to them too when you save.',
            )
          : t(
              'stories.conflicts.parkedToast',
              'Someone changed this while you were editing. Your version went to the owner to decide, and the page now shows the latest.',
            ),
        { duration: 8000 },
      );
      setPendingKey((k) => k + 1);
    }
    if (results.every((r) => r === 'saved')) {
      setStale(false);
      toast.success(t('stories.saved', 'Saved'));
      trackEngagement();
    } else if (results.includes('failed')) {
      toast.error(failure || t('stories.saveError', 'Failed to save'));
    }
    saveSeq.current++;
    setSaving(false);
  };

  // The first of the owner's own conflicts waits for their choice.
  const conflict = conflicts[0] ?? null;
  // Any "keep mine" in the queue needs a save once the last choice is made.
  const keptAny = useRef(false);
  const resolveOwn = (keepMine: boolean) => {
    if (!conflict) return;
    if (keepMine) {
      // Save again on top of what's there now; the text stays dirty until it lands.
      if (conflict.target === OWN) versions.current.chapter = conflict.current.version;
      else versions.current.beats[conflict.target] = conflict.current.version;
    } else {
      adopt(conflict.target, conflict.current);
    }
    setConflicts((prev) => prev.slice(1));
    if (keepMine) keptAny.current = true;
    // Once every choice is made, save what the owner chose to keep.
    if (conflicts.length === 1 && keptAny.current) {
      keptAny.current = false;
      setTimeout(() => saveRef.current(), 0);
    }
  };

  // Pick up other people's changes — but never over unsaved typing: the dirty check and
  // the apply happen together, after the requests, so nothing typed meanwhile is replaced.
  const dirtyNow = useRef(false);
  dirtyNow.current = chapterDirty || dirtyBeats.length > 0;
  const refreshIfClean = useCallback(
    async (onlyIfChanged: boolean) => {
      const seq = saveSeq.current;
      if (seq % 2 === 1) return; // a save is in flight; it reports anything that matters
      try {
        const [chapterRes, scenesRes] = await Promise.all([
          axios.get(`/stories/${id}/chapters/${chapterId}`),
          axios.get(`/stories/${id}/chapters/${chapterId}/scenes`),
        ]);
        const rows = scenesRes.data as Beat[];
        const known = versions.current;
        const changed =
          chapterRes.data.version !== known.chapter ||
          rows.some((b) => b.version !== known.beats[b.id]) ||
          rows.length !== Object.keys(known.beats).length;
        // Parked edits and decisions don't change any text; look for those every time.
        setPendingKey((k) => k + 1);
        if (onlyIfChanged && !changed) return;
        if (saveSeq.current !== seq) return;
        if (dirtyNow.current) {
          if (changed) setStale(true);
        } else {
          applyChapter(chapterRes.data);
          applyScenes(rows);
        }
      } catch {
        // Offline or signed out; the next save reports anything that matters.
      }
    },
    [id, chapterId, applyChapter, applyScenes],
  );

  // Coming back to the tab: catch up, or warn before a save collides.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible') refreshIfClean(true);
    };
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, [refreshIfClean]);

  const partLabel = (sceneId: string | null) => {
    if (!sceneId) return title || t('stories.conflicts.thisPage', 'this page');
    const idx = scenes.findIndex((b) => b.id === sceneId);
    return idx >= 0 ? `${subLabel} ${idx + 1}` : subLabel;
  };

  // Save on Ctrl+S
  const saveRef = useRef(save);
  saveRef.current = save;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (dirtyRef.current && canEditRef.current) saveRef.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Track the focused field and its text selection
  const handleSelect = (target: string) => (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    setActiveTarget(target);
    setSelection({ target, text: el.value.slice(el.selectionStart, el.selectionEnd) });
  };

  const jumpToBeat = (beatId: string) => {
    setReadMode(false);
    setActiveTarget(beatId);
    requestAnimationFrame(() => {
      const el = beatRefs.current[beatId];
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus({ preventScroll: true });
    });
  };

  // AI actions
  const runAi = async (mode: AiMode) => {
    if (!mode) return;
    setAiMode(mode);
    setAiLoading(true);
    setAiResult('');
    setAiOpen(true);

    try {
      let res;
      switch (mode) {
        case 'continue':
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/continue`, {
            mode: 'continue',
            instructions: aiInstruction || undefined,
          });
          break;
        case 'suggest':
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/suggest`, {
            mode: 'suggest',
          });
          break;
        case 'edit':
          if (!selectedText) {
            toast.error(t('stories.ai.selectTextFirst', 'Select text in the editor first'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/edit`, {
            mode: 'edit',
            selectedText,
            instruction: aiInstruction || 'Improve this passage',
          });
          break;
        case 'dialogue':
          if (!selectedCharacterId) {
            toast.error(t('stories.ai.selectCharacter', 'Select a character first'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/dialogue`, {
            mode: 'dialogue',
            characterId: selectedCharacterId,
            context: aiInstruction || undefined,
          });
          break;
        case 'describe':
          if (!aiInstruction) {
            toast.error(t('stories.ai.enterInstruction', 'Enter a description instruction'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/describe`, {
            mode: 'describe',
            instruction: aiInstruction,
          });
          break;
      }
      setAiResult(res?.data?.content || '');
    } catch (err: any) {
      const oow = outOfWordsFrom(err);
      const shared = sharedWordsMessage(err, t);
      if (oow) {
        setAiOpen(false);
        setOutOfWords(oow);
      } else if (shared) {
        toast.error(shared);
      } else if (err?.response?.status === 503) {
        toast.error(t('stories.ai.busy', 'The AI is busy right now — try again in a moment'));
      } else {
        toast.error(t('stories.ai.error', 'AI request failed'));
      }
    } finally {
      setAiLoading(false);
    }
  };

  const insertAiResult = () => {
    if (!aiResult) return;
    // Lands in the field last worked in; with none, at the end of the pages (the last beat).
    const fallback = scenes.length ? scenes[scenes.length - 1].id : OWN;
    if (aiMode === 'edit' && selection?.text) {
      // Replace selected text
      const target = selection.target;
      const text = textOf(target);
      const idx = text.indexOf(selection.text);
      if (idx !== -1) {
        setTextOf(target, text.slice(0, idx) + aiResult + text.slice(idx + selection.text.length));
      } else {
        setTextOf(target, text + '\n\n' + aiResult);
      }
    } else {
      const target =
        activeTarget && (activeTarget === OWN || beatText[activeTarget] !== undefined)
          ? activeTarget
          : fallback;
      const text = textOf(target);
      setTextOf(target, text + (text ? '\n\n' : '') + aiResult);
    }
    setAiResult('');
    setAiInstruction('');
    toast.success(t('stories.ai.inserted', 'Inserted'));
  };

  if (loading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ background: 'var(--ds-bg-primary)' }}
      >
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--ds-bg-primary)' }}>
      {/* Header */}
      <header
        className="flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
      >
        <button onClick={() => navigate(`/stories/${id}`)}>
          <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
        </button>
        <input
          type="text"
          value={title}
          readOnly={!canEdit}
          onChange={(e) => {
            setTitle(e.target.value);
            setChapterDirty(true);
          }}
          placeholder={t('stories.chapterTitle', 'Chapter title...')}
          className="flex-1 min-w-[10rem] bg-transparent text-lg font-semibold focus:outline-none"
          style={{ color: 'var(--ds-text-primary)' }}
        />
        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          <select
            value={status}
            disabled={!canEdit}
            onChange={(e) => {
              setStatus(e.target.value);
              setChapterDirty(true);
            }}
            className="text-xs px-2 py-1 rounded-lg border bg-transparent"
            style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
          >
            {['planned', 'in_progress', 'draft', 'revised', 'final'].map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
          {canEdit && scenes.length > 0 && (
            <button
              onClick={() => setReadMode(!readMode)}
              className={`p-2 rounded-lg transition-colors ${readMode ? 'bg-primary-500/20 text-primary-400' : ''}`}
              style={!readMode ? { color: 'var(--ds-text-secondary)' } : {}}
              title={
                readMode
                  ? t('stories.scenes.editView', 'Edit')
                  : t('stories.scenes.readView', 'Read the whole thing')
              }
            >
              {readMode ? (
                <PencilSquareIcon className="w-5 h-5" />
              ) : (
                <BookOpenIcon className="w-5 h-5" />
              )}
            </button>
          )}
          {canEdit && (
            <>
              <button
                onClick={() => setScenesOpen(!scenesOpen)}
                className={`p-2 rounded-lg transition-colors ${scenesOpen ? 'bg-primary-500/20 text-primary-400' : ''}`}
                style={!scenesOpen ? { color: 'var(--ds-text-secondary)' } : {}}
                title={subsTitle}
              >
                <ListBulletIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setAiOpen(!aiOpen)}
                className={`p-2 rounded-lg transition-colors ${aiOpen ? 'bg-primary-500/20 text-primary-400' : ''}`}
                style={!aiOpen ? { color: 'var(--ds-text-secondary)' } : {}}
                title={t('stories.ai.toggle', 'AI Assistant')}
              >
                <SparklesIcon className="w-5 h-5" />
              </button>
              <button
                onClick={save}
                disabled={saving || !dirty}
                className="px-4 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors"
              >
                {saving ? '...' : t('common.save', 'Save')}
              </button>
            </>
          )}
        </div>
      </header>

      {lastEdit && (
        <div className="px-4 pt-2 text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
          {t('stories.conflicts.lastEdited', 'Last edited by {{name}} · {{when}}', {
            name: lastEdit.name,
            when: new Date(lastEdit.at).toLocaleString(),
          })}
        </div>
      )}
      {stale && (
        <div
          className="mx-4 mt-2 rounded-lg px-3 py-2 text-xs"
          style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
        >
          {isOwner
            ? t(
                'stories.conflicts.staleOwner',
                'Someone changed this page while you were away. When you save, you can choose which version to keep.',
              )
            : t(
                'stories.conflicts.staleEditor',
                'Someone changed this page while you were away. If you save, your version goes to the owner to decide.',
              )}
        </div>
      )}
      {canEdit && id && chapterId && (
        <PendingEdits
          storyId={id}
          chapterId={chapterId}
          isOwner={isOwner}
          dirty={dirty}
          reloadKey={pendingKey}
          partLabel={partLabel}
          onApplied={() => refreshIfClean(false)}
        />
      )}
      {conflict && (
        <SaveConflictDialog
          part={partLabel(conflict.target === OWN ? null : conflict.target)}
          current={conflict.current}
          currentUserId={user?.id}
          onKeepMine={() => resolveOwn(true)}
          onTakeTheirs={() => resolveOwn(false)}
        />
      )}

      {/* Scene beats panel (collapsible top section) */}
      {scenesOpen && (
        <div
          className="border-b px-4 py-3 max-h-48 overflow-y-auto"
          style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--ds-text-secondary)' }}>
              {subsTitle}
            </span>
            <button onClick={() => setScenesOpen(false)}>
              <XMarkIcon className="w-4 h-4" style={{ color: 'var(--ds-text-secondary)' }} />
            </button>
          </div>
          {scenes.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              {acted
                ? t('stories.scenes.emptyBeatsActed', 'No beats planned for this scene.')
                : t('stories.scenes.emptyBeats', 'No scenes planned for this chapter.')}
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {scenes.map((scene, idx) => (
                <button
                  key={scene.id}
                  type="button"
                  onClick={() => jumpToBeat(scene.id)}
                  className="shrink-0 w-48 rounded-lg p-2.5 text-xs text-left hover:border-primary-500 transition-colors"
                  style={{
                    background: 'var(--ds-bg-primary)',
                    border: '1px solid var(--ds-border)',
                    color: 'var(--ds-text-primary)',
                  }}
                >
                  <div className="font-medium mb-1 flex items-center justify-between gap-2">
                    <span>
                      {subLabel} {idx + 1}
                    </span>
                    <span
                      className="text-[10px] font-normal"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {computeWords(beatText[scene.id] ?? '').toLocaleString()}{' '}
                      {t('stories.words', 'words')}
                    </span>
                  </div>
                  <div className="line-clamp-2" style={{ color: 'var(--ds-text-secondary)' }}>
                    {scene.summary || t('stories.scenes.untitled', 'Untitled scene')}
                  </div>
                  {scene.emotionalArc && (
                    <div
                      className="mt-1 text-[10px] italic"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {scene.emotionalArc}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor */}
        {scenes.length === 0 && canEdit ? (
          <div className={`flex-1 overflow-hidden ${aiOpen ? 'md:w-[65%]' : 'w-full'}`}>
            <textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              onSelect={handleSelect(OWN)}
              onFocus={() => setActiveTarget(OWN)}
              className="w-full h-full p-6 bg-transparent resize-none focus:outline-none text-base leading-relaxed"
              style={{ color: 'var(--ds-text-primary)' }}
              placeholder={t('stories.startWriting', 'Start writing...')}
            />
          </div>
        ) : readMode || !canEdit ? (
          <div className={`flex-1 overflow-y-auto ${aiOpen ? 'md:w-[65%]' : 'w-full'}`}>
            <div
              className="p-6 text-base leading-relaxed whitespace-pre-wrap"
              style={{ color: 'var(--ds-text-primary)' }}
            >
              {[content, ...scenes.map((b) => beatText[b.id] ?? '')]
                .filter((part) => part.trim())
                .join('\n\n') || t('stories.scenes.notDrafted', 'Nothing drafted yet.')}
            </div>
          </div>
        ) : (
          <div className={`flex-1 overflow-y-auto ${aiOpen ? 'md:w-[65%]' : 'w-full'}`}>
            <div className="p-6 space-y-6">
              <section>
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {acted
                    ? t('stories.scenes.beforeFirstBeat', 'Before the first beat')
                    : t('stories.scenes.beforeFirstScene', 'Before the first scene')}
                </div>
                <AutoTextarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  onSelect={handleSelect(OWN)}
                  onFocus={() => setActiveTarget(OWN)}
                  rows={2}
                  className="w-full bg-transparent resize-none focus:outline-none text-base leading-relaxed"
                  style={{ color: 'var(--ds-text-primary)' }}
                  placeholder={t('stories.startWriting', 'Start writing...')}
                />
              </section>
              {scenes.map((scene, idx) => (
                <section
                  key={scene.id}
                  className="border-t pt-4"
                  style={{ borderColor: 'var(--ds-border)' }}
                >
                  <div className="mb-2">
                    <div
                      className="text-[11px] font-semibold uppercase tracking-wide"
                      style={{
                        color:
                          activeTarget === scene.id ? 'var(--ds-link)' : 'var(--ds-text-secondary)',
                      }}
                    >
                      {subLabel} {idx + 1}
                      {dirtyBeats.includes(scene.id) && <span className="text-yellow-400"> •</span>}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
                      {scene.summary || t('stories.scenes.untitled', 'Untitled scene')}
                    </div>
                  </div>
                  <AutoTextarea
                    ref={(el) => {
                      beatRefs.current[scene.id] = el;
                    }}
                    value={beatText[scene.id] ?? ''}
                    onChange={(e) => handleBeatChange(scene.id, e.target.value)}
                    onSelect={handleSelect(scene.id)}
                    onFocus={() => setActiveTarget(scene.id)}
                    className="w-full bg-transparent resize-none focus:outline-none text-base leading-relaxed"
                    style={{ color: 'var(--ds-text-primary)' }}
                    placeholder={t('stories.scenes.notDrafted', 'Nothing drafted yet.')}
                  />
                </section>
              ))}
            </div>
          </div>
        )}

        {/* AI Panel */}
        {canEdit && aiOpen && (
          <div
            className="w-full md:w-[35%] border-l flex flex-col overflow-hidden"
            style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'var(--ds-border)' }}
            >
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-primary-400" />
                <span className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                  {t('stories.ai.title', 'AI Assistant')}
                </span>
              </div>
              <button onClick={() => setAiOpen(false)}>
                <XMarkIcon className="w-4 h-4" style={{ color: 'var(--ds-text-secondary)' }} />
              </button>
            </div>

            {/* Quick actions */}
            <div className="px-4 py-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { mode: 'continue' as AiMode, label: t('stories.ai.continue', 'Continue') },
                    { mode: 'suggest' as AiMode, label: t('stories.ai.suggest', 'Suggest') },
                    { mode: 'describe' as AiMode, label: t('stories.ai.describe', 'Describe') },
                    {
                      mode: 'edit' as AiMode,
                      label: t('stories.ai.editSelection', 'Edit Selection'),
                    },
                    { mode: 'dialogue' as AiMode, label: t('stories.ai.dialogue', 'Dialogue') },
                  ] as const
                ).map((action) => (
                  <button
                    key={action.mode}
                    onClick={() => runAi(action.mode)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border hover:border-primary-500 hover:text-primary-400 disabled:opacity-50"
                    style={{
                      borderColor: 'var(--ds-border)',
                      color: 'var(--ds-text-secondary)',
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>

              {/* Character selector for dialogue */}
              {characters.length > 0 && (
                <select
                  value={selectedCharacterId}
                  onChange={(e) => setSelectedCharacterId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-transparent text-xs"
                  style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                >
                  <option value="">
                    {t('stories.ai.selectCharacterOption', '— Character for dialogue —')}
                  </option>
                  {characters.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Instruction input */}
              <input
                type="text"
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder={t('stories.ai.instructionPlaceholder', 'Instructions (optional)...')}
                className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !aiLoading) {
                    runAi(aiMode || 'continue');
                  }
                }}
              />

              {selectedText && (
                <div className="text-[11px] px-2 py-1 rounded bg-yellow-500/10 text-yellow-400 truncate">
                  {t('stories.ai.selected', 'Selected')}: "{selectedText.slice(0, 80)}
                  {selectedText.length > 80 ? '...' : ''}"
                </div>
              )}
            </div>

            {/* Result area */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {aiLoading && (
                <div className="flex items-center justify-center py-12">
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                    style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
                  />
                </div>
              )}

              {!aiLoading && aiResult && (
                <div className="space-y-3">
                  <div
                    className="text-sm leading-relaxed whitespace-pre-wrap rounded-xl p-4"
                    style={{
                      background: 'var(--ds-bg-primary)',
                      color: 'var(--ds-text-primary)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    {aiResult}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={insertAiResult}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-lg font-medium transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      {t('stories.ai.insert', 'Insert')}
                    </button>
                    <button
                      onClick={() => {
                        setAiResult('');
                        setAiInstruction('');
                      }}
                      className="px-4 py-2 rounded-lg text-sm border"
                      style={{
                        borderColor: 'var(--ds-border)',
                        color: 'var(--ds-text-secondary)',
                      }}
                    >
                      {t('stories.ai.discard', 'Discard')}
                    </button>
                    <button
                      onClick={() => runAi(aiMode)}
                      className="p-2 rounded-lg border"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                      title={t('stories.ai.retry', 'Retry')}
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {!aiLoading && !aiResult && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t(
                    'stories.ai.hint',
                    'Click a quick action or type instructions and press Enter.',
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer
        className="flex items-center justify-between px-6 py-2 border-t text-xs"
        style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
      >
        <span>
          {wordCount.toLocaleString()} {t('stories.words', 'words')}
        </span>
        {dirty && (
          <span className="text-yellow-400">{t('stories.unsaved', 'Unsaved changes')}</span>
        )}
      </footer>
      {outOfWords && <OutOfWordsSheet info={outOfWords} onClose={() => setOutOfWords(null)} />}
    </div>
  );
};

export default ChapterEditor;
